/**
 * Pure, side-effect-free transform functions for simulation result data.
 *
 * This module is intentionally free of browser APIs, auth helpers, and fetch calls
 * so it can safely be imported by both the main thread and a Web Worker.
 */

import type { RideSchema, SimulationActivity } from '../../types/simulation';
import { inferActivityStopKind } from '../../utils/simulation/inferActivityStopKind';
import { parseISO } from 'date-fns';

// ---------------------------------------------------------------------------
// Activity time helpers
// ---------------------------------------------------------------------------

/** Instants before 1970-01-02 UTC are treated as placeholder (epoch) start times from the API. */
const EPOCH_SENTINEL_MS_MAX = 86_400_000;

function isEpochSentinelIso(iso: unknown): boolean {
  if (typeof iso !== 'string' || !iso) return false;
  const t = Date.parse(iso);
  return Number.isFinite(t) && t < EPOCH_SENTINEL_MS_MAX;
}

/**
 * Prefer a real stop/trip time when the stop-level activity_start_time is a sentinel (e.g. 1970-01-01).
 *
 * Parameters:
 *   item (Record<string, unknown>): Raw activity object from the API.
 *
 * Returns:
 *   string | undefined — resolved ISO timestamp, or undefined if none available.
 */
function resolveEffectiveActivityStartTime(item: Record<string, unknown>): string | undefined {
  const start = item.activity_start_time;
  if (typeof start === 'string' && start && !isEpochSentinelIso(start)) return start;
  const end = item.activity_end_time;
  if (typeof end === 'string' && end && !isEpochSentinelIso(end)) return end;
  const trips = item.trips;
  if (Array.isArray(trips)) {
    for (const trip of trips) {
      if (!trip || typeof trip !== 'object') continue;
      const tr = trip as Record<string, unknown>;
      const ts = tr.activity_start_time ?? tr.activity_end_time;
      if (typeof ts === 'string' && ts && !isEpochSentinelIso(ts)) return ts;
    }
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Coordinate / ID / time helpers
// ---------------------------------------------------------------------------

export const candidateArrays = (source: any, keys: string[]): any[] => {
  for (const key of keys) {
    const value = source?.[key];
    if (Array.isArray(value)) return value;
  }
  return [];
};

const getCoordinate = (obj: any, lat: boolean): number | undefined => {
  const keys = lat
    ? ['lat', 'latitude', 'activity_latitude', 'from_latitude', 'pickup_latitude', 'origin_latitude']
    : ['lng', 'lon', 'longitude', 'activity_longitude', 'from_longitude', 'pickup_longitude', 'origin_longitude'];
  for (const key of keys) {
    const value = obj?.[key];
    if (typeof value === 'number') return value;
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }
  return undefined;
};

const getDestinationCoordinate = (obj: any, lat: boolean): number | undefined => {
  const keys = lat
    ? ['to_latitude', 'dropoff_latitude', 'destination_latitude']
    : ['to_longitude', 'dropoff_longitude', 'destination_longitude'];
  for (const key of keys) {
    const value = obj?.[key];
    if (typeof value === 'number') return value;
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }
  return undefined;
};

const normalizeRideId = (obj: any): string | undefined =>
  obj?.ride_id ||
  obj?.driver_ride_id ||
  obj?.request_id ||
  obj?.rider_ride_id ||
  obj?.rider_id ||
  obj?.rider ||
  obj?.external_user_reference_id ||
  obj?.user_id ||
  obj?.id;

const normalizeTime = (value: any): string | undefined => {
  if (typeof value === 'string' && value) {
    const maybeDate = parseISO(value);
    if (!Number.isNaN(maybeDate.getTime())) return value;
  }
  return undefined;
};

const toNumber = (value: any): number | undefined => {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return undefined;
};

const firstNonEmptyString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (trimmed.length > 0) return trimmed;
  }
  return undefined;
};

function extractAddressFromLocationNode(node: unknown): string | undefined {
  if (!node || typeof node !== 'object') return undefined;
  const rec = node as Record<string, unknown>;
  return firstNonEmptyString(
    rec.address,
    rec.address_formatted,
    rec.label,
    rec.name,
    rec.location_name,
    rec.stop_name
  );
}

function extractRideAddresses(ride: unknown): { from_address?: string; to_address?: string } {
  if (!ride || typeof ride !== 'object') return {};
  const rec = ride as Record<string, unknown>;
  return {
    from_address: firstNonEmptyString(
      rec.from_address,
      rec.fromAddress,
      rec.pickup_address,
      rec.pickupAddress,
      rec.origin_address,
      rec.originAddress,
      rec.departure_address,
      rec.departureAddress,
      extractAddressFromLocationNode(rec.origin),
      extractAddressFromLocationNode(rec.pickup),
      extractAddressFromLocationNode(rec.departure)
    ),
    to_address: firstNonEmptyString(
      rec.to_address,
      rec.toAddress,
      rec.dropoff_address,
      rec.dropoffAddress,
      rec.destination_address,
      rec.destinationAddress,
      rec.arrival_address,
      rec.arrivalAddress,
      extractAddressFromLocationNode(rec.destination),
      extractAddressFromLocationNode(rec.dropoff),
      extractAddressFromLocationNode(rec.arrival)
    ),
  };
}

function extractActivityStopAddress(activity: unknown): string | undefined {
  if (!activity || typeof activity !== 'object') return undefined;
  const rec = activity as Record<string, unknown>;
  return firstNonEmptyString(
    rec.address,
    rec.address_formatted,
    rec.location_name,
    rec.locationName,
    rec.stop_name,
    rec.stopName,
    rec.label,
    rec.name
  );
}

const extractStopNumber = (activity: any, fallback: number): number => {
  const candidates = [
    activity?.stop_number,
    activity?.stop_visit_id,
    activity?.driver_activity_number,
    activity?.activity_sequence,
  ];
  for (const value of candidates) {
    const parsed = toNumber(value);
    if (typeof parsed === 'number') return parsed;
  }
  return fallback;
};

const extractTimestamp = (activity: any): number => {
  const fields = [
    activity?.activity_start_time,
    activity?.activity_start,
    activity?.min_activity_start_time,
    activity?.start_time,
    activity?.time,
  ];
  for (const field of fields) {
    if (field) {
      const parsed = Date.parse(field);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }
  return Number.MAX_SAFE_INTEGER;
};

// ---------------------------------------------------------------------------
// Main transforms (exported for worker + resultsApi)
// ---------------------------------------------------------------------------

/**
 * Normalise raw activity objects from the /activities endpoint.
 * Derives activity_type from ride counts, user id arrays, and type strings
 * so Operations and exports stay consistent.
 *
 * Parameters:
 *   raw (any[]): Array of raw activity objects from the API.
 *
 * Returns:
 *   SimulationActivity[]
 */
export function normalizeActivitiesFromApi(raw: any[]): SimulationActivity[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => {
    const rec = item as Record<string, unknown>;
    const resolvedStart = resolveEffectiveActivityStartTime(rec);
    const base = resolvedStart ? { ...item, activity_start_time: resolvedStart } : item;
    const act = { ...base } as SimulationActivity;
    const kind = inferActivityStopKind(act);
    let activity_type = (item?.activity_type ?? item?.type ?? '').toString().trim();
    if (kind === 'pick') activity_type = 'pickup';
    else if (kind === 'drop') activity_type = 'dropoff';
    return {
      ...base,
      activity_type: activity_type || undefined,
      type: activity_type || item?.type,
      vehicle_id: item?.vehicle_id ?? item?.vehicle_shift_id ?? item?.shift_id,
    } as SimulationActivity;
  });
}

/**
 * Parse ride origin/destination points from the /integration/solution payload.
 *
 * Parameters:
 *   solutionPayload (any): Raw solution JSON from the API.
 *
 * Returns:
 *   RideSchema[]
 */
export const parseRidePointsFromSolution = (solutionPayload: any): RideSchema[] => {
  if (!solutionPayload) return [];
  const rideMap = new Map<string, Partial<RideSchema>>();

  const ensureRide = (rideId: string) => {
    if (!rideMap.has(rideId)) {
      rideMap.set(rideId, {
        passengers: [{ number_of_passengers: 1, type: 'standard' }],
      });
    }
    return rideMap.get(rideId)!;
  };

  const ingestRideObject = (ride: any) => {
    const rideId = normalizeRideId(ride);
    if (!rideId) return;
    const target = ensureRide(rideId);
    target.external_reference_id = rideId;
    target.from_latitude = target.from_latitude ?? getCoordinate(ride, true) ?? ride?.origin?.latitude;
    target.from_longitude = target.from_longitude ?? getCoordinate(ride, false) ?? ride?.origin?.longitude;
    target.to_latitude = target.to_latitude ?? getDestinationCoordinate(ride, true) ?? ride?.destination?.latitude;
    target.to_longitude = target.to_longitude ?? getDestinationCoordinate(ride, false) ?? ride?.destination?.longitude;
    const addresses = extractRideAddresses(ride);
    target.from_address = target.from_address ?? addresses.from_address;
    target.to_address = target.to_address ?? addresses.to_address;
    target.request_time = target.request_time ?? normalizeTime(ride?.request_time || ride?.time);
  };

  const ingestActivity = (activity: any) => {
    const trips = Array.isArray(activity?.trips) ? activity.trips : [];
    trips.forEach((trip: any) => {
      if (trip?.ride && typeof trip.ride === 'object') {
        const mergedRide = { ...trip.ride, ride_id: normalizeRideId(trip.ride) ?? normalizeRideId(trip) };
        ingestRideObject(mergedRide);
      }
    });

    const rideId = normalizeRideId(activity);
    if (!rideId) return;
    const activityType = (activity?.activity_type || activity?.type || '').toString().toLowerCase();
    const lat = getCoordinate(activity, true);
    const lon = getCoordinate(activity, false);
    if (typeof lat !== 'number' || typeof lon !== 'number') return;
    const target = ensureRide(rideId);
    const stopAddress = extractActivityStopAddress(activity);
    if (activityType.includes('pick') || activityType.includes('origin') || activityType === 'pickup') {
      target.from_latitude = target.from_latitude ?? lat;
      target.from_longitude = target.from_longitude ?? lon;
      target.from_address = target.from_address ?? stopAddress;
      target.request_time = target.request_time ?? normalizeTime(activity?.activity_start_time || activity?.time);
    } else if (activityType.includes('drop') || activityType.includes('dest') || activityType === 'dropoff') {
      target.to_latitude = target.to_latitude ?? lat;
      target.to_longitude = target.to_longitude ?? lon;
      target.to_address = target.to_address ?? stopAddress;
    }
  };

  const directRides = candidateArrays(solutionPayload, ['rides', 'requests']);
  directRides.forEach(ingestRideObject);

  const routes = candidateArrays(solutionPayload, ['routes']);
  if (routes.length === 0 && solutionPayload?.task_result) {
    routes.push(...candidateArrays(solutionPayload.task_result, ['routes']));
  }
  routes.forEach(route => {
    const activityLists = ['activities', 'stops', 'events', 'timeline']
      .map(key => route?.[key])
      .filter(Array.isArray);
    activityLists.forEach(list => list.forEach(ingestActivity));
    const rideList = ['rides', 'requests'].map(key => route?.[key]).filter(Array.isArray);
    rideList.forEach(list => list.forEach(ingestRideObject));
  });

  const solutionData = solutionPayload.solution || solutionPayload.data;
  if (solutionData) {
    candidateArrays(solutionData, ['rides', 'requests']).forEach(ingestRideObject);
    candidateArrays(solutionData, ['routes']).forEach(route => {
      ['activities', 'stops'].forEach(key => {
        const arr = route?.[key];
        if (Array.isArray(arr)) arr.forEach(ingestActivity);
      });
    });
  }

  return Array.from(rideMap.entries())
    .map(([rideId, ride]) => ({
      ride_id: rideId,
      from_latitude: ride.from_latitude ?? 0,
      from_longitude: ride.from_longitude ?? 0,
      to_latitude: ride.to_latitude ?? ride.from_latitude ?? 0,
      to_longitude: ride.to_longitude ?? ride.from_longitude ?? 0,
      from_address: ride.from_address,
      to_address: ride.to_address,
      request_time: ride.request_time,
      passengers: ride.passengers || [{ number_of_passengers: 1, type: 'standard' }],
      external_reference_id: ride.external_reference_id || rideId,
      external_user_reference_id: ride.external_user_reference_id,
    }))
    .filter(ride => typeof ride.from_latitude === 'number' && typeof ride.to_latitude === 'number');
};

/** Map one solution activity (with type START/PICK/DROP) to SimulationActivity for Operations view */
function mapSolutionActivityToSimulation(
  act: any,
  vehicleId: string,
  sequenceIndex: number
): SimulationActivity {
  const rawType = (act?.type ?? act?.activity_type ?? '').toString().toUpperCase();
  const activityType =
    rawType === 'PICK' ? 'pickup' : rawType === 'DROP' ? 'dropoff' : rawType.toLowerCase() || 'other';
  const time =
    act?.start_time ?? act?.activity_start_time ?? act?.arrival_time ?? act?.end_time ?? act?.time;
  const lat = getCoordinate(act, true);
  const lng = getCoordinate(act, false);
  const rawAct = act as Record<string, unknown>;
  const trips = Array.isArray(rawAct.trips) ? rawAct.trips : undefined;
  return {
    vehicle_id: vehicleId,
    vehicle_shift_id: vehicleId,
    activity_type: activityType,
    type: activityType,
    activity_start_time: normalizeTime(time) ?? (typeof time === 'string' ? time : undefined),
    start_time: typeof time === 'string' ? time : undefined,
    activity_latitude: typeof lat === 'number' ? lat : undefined,
    activity_longitude: typeof lng === 'number' ? lng : undefined,
    latitude: typeof lat === 'number' ? lat : undefined,
    longitude: typeof lng === 'number' ? lng : undefined,
    stop_number: act?.stop_number ?? sequenceIndex,
    stop_visit_id: act?.stop_visit_id != null ? String(act.stop_visit_id) : undefined,
    ride_id: normalizeRideId(act) ?? act?.rider_ride_id,
    request_id: act?.request_id ?? normalizeRideId(act),
    address: extractActivityStopAddress(act),
    location_name: firstNonEmptyString(rawAct.location_name, rawAct.locationName),
    stop_name: firstNonEmptyString(rawAct.stop_name, rawAct.stopName),
    ...(trips ? { trips } : {}),
  };
}

/**
 * Parse activities from the integration/solution payload for the Operations view.
 *
 * Parameters:
 *   solutionPayload (any): Raw solution JSON from the API.
 *
 * Returns:
 *   SimulationActivity[]
 */
export function parseActivitiesFromSolution(solutionPayload: any): SimulationActivity[] {
  if (!solutionPayload) return [];
  const payload = solutionPayload.solution ?? solutionPayload.data ?? solutionPayload;
  const activities: SimulationActivity[] = [];

  const obr = payload?.on_demand_bus_result;
  if (obr?.solutions && Array.isArray(obr.solutions)) {
    obr.solutions.forEach((sol: any, solIdx: number) => {
      const matches = candidateArrays(sol, ['matches']);
      matches.forEach((match: any, matchIdx: number) => {
        const vehicleId =
          match?.vehicle_id ??
          match?.driver_id ??
          match?.vehicle_shift_id ??
          match?.shift_id ??
          `solution-${solIdx}-match-${matchIdx}`;
        const route = match?.route;
        const activityList = candidateArrays(route ?? {}, ['activities', 'stops', 'events', 'timeline']);
        activityList.forEach((act: any) => {
          activities.push(mapSolutionActivityToSimulation(act, String(vehicleId), activities.length));
        });
      });
    });
  }

  if (activities.length === 0) {
    const routes = candidateArrays(payload, ['routes']);
    const taskRoutes = payload?.task_result ? candidateArrays(payload.task_result, ['routes']) : [];
    const allRoutes = routes.length ? routes : taskRoutes;
    allRoutes.forEach((route: any, routeIdx: number) => {
      const vehicleId =
        route?.vehicle_id ??
        route?.driver_id ??
        route?.vehicle_shift_id ??
        route?.shift_id ??
        `route-${routeIdx}`;
      ['activities', 'stops', 'events', 'timeline'].forEach(key => {
        const arr = route?.[key];
        if (Array.isArray(arr)) {
          arr.forEach((act: any) => {
            activities.push(mapSolutionActivityToSimulation(act, String(vehicleId), activities.length));
          });
        }
      });
    });

    const solutionData = payload?.solution ?? payload?.data;
    if (activities.length === 0 && solutionData) {
      candidateArrays(solutionData, ['routes']).forEach((route: any, routeIdx: number) => {
        const vehicleId =
          route?.vehicle_id ??
          route?.driver_id ??
          route?.vehicle_shift_id ??
          route?.shift_id ??
          `route-${routeIdx}`;
        ['activities', 'stops'].forEach(key => {
          const arr = route?.[key];
          if (Array.isArray(arr)) {
            arr.forEach((act: any) => {
              activities.push(mapSolutionActivityToSimulation(act, String(vehicleId), activities.length));
            });
          }
        });
      });
    }
  }

  return activities;
}

/**
 * Derive ride segment polylines from activities when solution ride points are unavailable.
 * Groups by vehicle, sorts by stop number / timestamp, then connects consecutive **passenger** stops only
 * (both endpoints classified as pick or drop by inferActivityStopKind — excludes depot/shift start/end legs).
 *
 * Parameters:
 *   activities (any[]): Normalized activity objects.
 *
 * Returns:
 *   RideSchema[]
 */
export const buildRidePointsFromActivities = (activities: any[]): RideSchema[] => {
  if (!Array.isArray(activities) || activities.length === 0) return [];

  // Primary path: build per-rider rides from activities[].trips[] so mixed PICK/DROP stops
  // do not create synthetic "pickup at dropoff" artifacts.
  const rideById = new Map<string, Partial<RideSchema>>();
  const ensureTripRide = (rideId: string): Partial<RideSchema> => {
    if (!rideById.has(rideId)) {
      rideById.set(rideId, {
        ride_id: rideId,
        external_reference_id: rideId,
        passengers: [{ number_of_passengers: 1, type: 'standard' }],
      });
    }
    return rideById.get(rideId)!;
  };

  activities.forEach((activity) => {
    const activityRec = activity as Record<string, unknown>;
    const lat = toNumber(activityRec?.activity_latitude) ?? toNumber(activityRec?.latitude) ?? toNumber(activityRec?.lat);
    const lng = toNumber(activityRec?.activity_longitude) ?? toNumber(activityRec?.longitude) ?? toNumber(activityRec?.lng);
    const trips = Array.isArray(activityRec?.trips) ? (activityRec.trips as Array<Record<string, unknown>>) : [];
    if (trips.length === 0) return;

    trips.forEach((trip) => {
      const rideObj = (trip.ride && typeof trip.ride === 'object') ? (trip.ride as Record<string, unknown>) : undefined;
      const rideId =
        normalizeRideId(rideObj) ??
        normalizeRideId(trip) ??
        (typeof trip.rider_ride_id === 'string' ? trip.rider_ride_id : undefined);
      if (!rideId) return;

      const target = ensureTripRide(rideId);
      const requestTime = normalizeTime(rideObj?.request_time ?? trip.activity_start_time ?? activityRec.activity_start_time ?? activityRec.time);

      if (rideObj) {
        target.external_reference_id = (typeof rideObj.external_reference_id === 'string' && rideObj.external_reference_id) || target.external_reference_id || rideId;
        target.external_user_reference_id =
          (typeof rideObj.external_user_reference_id === 'string' && rideObj.external_user_reference_id) ||
          (typeof activityRec.user_id === 'string' ? activityRec.user_id : undefined) ||
          target.external_user_reference_id;
        target.from_latitude = target.from_latitude ?? getCoordinate(rideObj, true) ?? getCoordinate(activityRec, true);
        target.from_longitude = target.from_longitude ?? getCoordinate(rideObj, false) ?? getCoordinate(activityRec, false);
        target.to_latitude = target.to_latitude ?? getDestinationCoordinate(rideObj, true);
        target.to_longitude = target.to_longitude ?? getDestinationCoordinate(rideObj, false);
        const addresses = extractRideAddresses(rideObj);
        target.from_address = target.from_address ?? addresses.from_address;
        target.to_address = target.to_address ?? addresses.to_address;
        target.request_type =
          (typeof rideObj.request_type === 'string' && rideObj.request_type) ||
          (typeof target.request_type === 'string' ? target.request_type : undefined);
        if (Array.isArray(rideObj.passengers) && rideObj.passengers.length > 0) {
          target.passengers = rideObj.passengers as RideSchema['passengers'];
        }
      }

      const tripType = String(trip.activity_type ?? activityRec.activity_type ?? activityRec.type ?? '').toUpperCase();
      if (tripType.includes('PICK')) {
        if (typeof lat === 'number' && typeof lng === 'number') {
          target.from_latitude = target.from_latitude ?? lat;
          target.from_longitude = target.from_longitude ?? lng;
        }
        target.request_time = target.request_time ?? requestTime;
      } else if (tripType.includes('DROP')) {
        if (typeof lat === 'number' && typeof lng === 'number') {
          target.to_latitude = target.to_latitude ?? lat;
          target.to_longitude = target.to_longitude ?? lng;
        }
      }
    });
  });

  const tripLevelRides = Array.from(rideById.values())
    .map((ride) => ({
      ride_id: ride.ride_id,
      from_latitude: ride.from_latitude,
      from_longitude: ride.from_longitude,
      to_latitude: ride.to_latitude,
      to_longitude: ride.to_longitude,
      from_address: ride.from_address,
      to_address: ride.to_address,
      request_time: ride.request_time,
      request_type: ride.request_type,
      passengers: ride.passengers || [{ number_of_passengers: 1, type: 'standard' }],
      external_reference_id: ride.external_reference_id || ride.ride_id,
      external_user_reference_id: ride.external_user_reference_id,
    }))
    .filter((ride) =>
      typeof ride.ride_id === 'string' &&
      typeof ride.from_latitude === 'number' &&
      typeof ride.from_longitude === 'number' &&
      typeof ride.to_latitude === 'number' &&
      typeof ride.to_longitude === 'number'
    ) as RideSchema[];

  if (tripLevelRides.length > 0) {
    return tripLevelRides;
  }

  const groups = new Map<string, any[]>();
  activities.forEach(activity => {
    const key =
      activity?.vehicle_shift_id ||
      activity?.shift_id ||
      activity?.vehicle_id ||
      'global';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(activity);
  });

  const segments: RideSchema[] = [];
  groups.forEach((list) => {
    list.sort((a, b) => {
      const stopDiff = extractStopNumber(a, 0) - extractStopNumber(b, 0);
      if (stopDiff !== 0) return stopDiff;
      return extractTimestamp(a) - extractTimestamp(b);
    });

    // Only consecutive passenger stops (pick/drop); skip deadhead segments (e.g. depot→first pickup, last drop→shift end).
    for (let i = 1; i < list.length; i++) {
      const prev = list[i - 1];
      const curr = list[i];
      const prevKind = inferActivityStopKind(prev as SimulationActivity);
      const currKind = inferActivityStopKind(curr as SimulationActivity);
      if (prevKind === 'other' || currKind === 'other') {
        continue;
      }
      const fromLat = toNumber(prev?.activity_latitude) ?? toNumber(prev?.latitude) ?? toNumber(prev?.lat);
      const fromLng = toNumber(prev?.activity_longitude) ?? toNumber(prev?.longitude) ?? toNumber(prev?.lng);
      const toLat = toNumber(curr?.activity_latitude) ?? toNumber(curr?.latitude) ?? toNumber(curr?.lat);
      const toLng = toNumber(curr?.activity_longitude) ?? toNumber(curr?.longitude) ?? toNumber(curr?.lng);
      if (
        typeof fromLat !== 'number' ||
        typeof fromLng !== 'number' ||
        typeof toLat !== 'number' ||
        typeof toLng !== 'number'
      ) {
        continue;
      }

      const key =
        prev?.vehicle_shift_id || prev?.shift_id || prev?.vehicle_id || 'global';
      const rideId = `${key}-${prev?.stop_visit_id ?? prev?.stop_number ?? i}-${curr?.stop_visit_id ?? curr?.stop_number ?? i + 1}`;
      segments.push({
        ride_id: rideId,
        from_latitude: fromLat,
        from_longitude: fromLng,
        to_latitude: toLat,
        to_longitude: toLng,
        request_time: prev?.activity_start_time || prev?.start_time || prev?.time,
        passengers: [{ number_of_passengers: 1, type: 'standard' }],
      });
    }
  });

  return segments;
};
